// src/utils/eth.js

const BSC = {
  CHAIN_ID_HEX: "0x38", // 56
  RPC: "https://bsc-dataseed.binance.org/",
};

/**
 * 统一从 window.ethereum 获取 provider
 */
export function getProvider() {
  const eth = window.ethereum;
  if (!eth || typeof eth.request !== "function") {
    throw new Error("未检测到钱包，请在支持 BSC 的钱包内打开");
  }
  return eth;
}

/**
 * 把各种奇怪格式的 chainId 统一成小写 0x 前缀字符串
 * 可能的情况：
 *  - '0x38'
 *  - '0X38'
 *  - 56
 *  - { chainId: '0x38' }
 *  - { result: '0x38' }
 */
function normalizeChainId(chainIdRaw) {
  let v = chainIdRaw;

  if (v && typeof v === "object") {
    v = v.chainId || v.result || v.data || null;
  }

  if (typeof v === "number") {
    v = "0x" + v.toString(16);
  }

  if (typeof v !== "string") return null;
  return v.toLowerCase();
}

/**
 * 确保当前在 BSC 网络
 * - 兼容 Trust 返回数字 / 对象导致的 toLowerCase 报错
 * - 尝试 wallet_switchEthereumChain / wallet_addEthereumChain
 */
export async function ensureBsc(provider = getProvider()) {
  const raw = await provider.request({ method: "eth_chainId" });
  const chainId = normalizeChainId(raw);

  // 已经是 BSC
  if (chainId === BSC.CHAIN_ID_HEX) return;

  try {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: BSC.CHAIN_ID_HEX }],
    });
  } catch (e) {
    // 链未添加，尝试添加网络
    if (e && e.code === 4902) {
      await provider.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: BSC.CHAIN_ID_HEX,
            chainName: "BNB Smart Chain",
            rpcUrls: [BSC.RPC],
            nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
            blockExplorerUrls: ["https://bscscan.com/"],
          },
        ],
      });
      return;
    }
    // 部分钱包（例如 Trust）不支持切链，抛出统一提示
    throw new Error("请切换到 BNB Smart Chain (BSC)");
  }
}

/**
 * 请求账户地址
 */
export async function requestAccounts(provider = getProvider()) {
  const accts = await provider.request({ method: "eth_requestAccounts" });
  const addr = accts && accts[0];
  if (!addr) throw new Error("未获取到钱包地址");
  return addr;
}

// ---------- 编码/解析工具 ----------

export function strip0x(h) {
  const s = h || "";
  return s.startsWith("0x") || s.startsWith("0X") ? s.slice(2) : s;
}

export function add0x(h) {
  const s = h || "";
  return s.startsWith("0x") || s.startsWith("0X") ? s : "0x" + s;
}

export function pad32(hexNo0x) {
  return hexNo0x.padStart(64, "0");
}

/**
 * 地址参数编码：左补零 32 字节
 * 为防止传入非字符串，先转成字符串再 toLowerCase
 */
export function addrParam(addr) {
  const s = String(addr || "");
  return pad32(strip0x(s).toLowerCase());
}

export function u256Param(bi) {
  const hex = bi.toString(16);
  return pad32(hex);
}

export function toBigInt(val) {
  if (typeof val === "bigint") return val;
  if (typeof val === "number") return BigInt(Math.trunc(val));
  if (typeof val === "string") {
    if (val.startsWith("0x") || val.startsWith("0X")) return BigInt(val);
    return BigInt(val); // 十进制字符串
  }
  return BigInt(0);
}

export function pow10(n) {
  return BigInt(10) ** BigInt(n);
}

export function formatUnits(bi, decimals, frac = 2) {
  const base = pow10(decimals);
  const i = bi / base;
  const f = bi % base;
  const scaled = (f * BigInt(10 ** frac)) / base;
  return `${i.toString()}.${scaled.toString().padStart(frac, "0")}`;
}

export function parseUnits(amountStrOrNum, decimals) {
  // 接受 '25' / '25.123' / 25
  const s = String(amountStrOrNum);
  if (!s.includes(".")) return toBigInt(s) * pow10(decimals);
  const [int, fracRaw] = s.split(".");
  const frac = fracRaw.slice(0, decimals).padEnd(decimals, "0"); // 截断/补零
  return toBigInt(int) * pow10(decimals) + toBigInt(frac);
}

// ---------- 读取/发送 ----------

export async function ethCall(
  callObj,
  tag = "latest",
  provider = getProvider()
) {
  const res = await provider.request({
    method: "eth_call",
    params: [callObj, tag],
  });
  return res; // hex string
}

export async function ethEstimateGas(txObj, provider = getProvider()) {
  return await provider.request({ method: "eth_estimateGas", params: [txObj] });
}

export async function ethGasPrice(provider = getProvider()) {
  return await provider.request({ method: "eth_gasPrice" });
}

export async function sendTransaction(txObj, provider = getProvider()) {
  const hash = await provider.request({
    method: "eth_sendTransaction",
    params: [txObj],
  });
  return { hash };
}

export async function getReceipt(txHash, provider = getProvider()) {
  return await provider.request({
    method: "eth_getTransactionReceipt",
    params: [txHash],
  });
}

export async function waitReceipt(
  txHash,
  { intervalMs = 1500, maxTries = 240 } = {},
  provider = getProvider()
) {
  let tries = 0;
  while (tries++ < maxTries) {
    const r = await getReceipt(txHash, provider);
    if (r && r.blockNumber) return r;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("等待交易确认超时");
}
